import { useEffect } from 'react';
import { GridElement, Connection, ElementAction } from '../types';
import { GpsBounds } from '../types/presenter.types';
import { convertDBDataNodeToGridElement, convertDBDataLinkToConnection } from '../utils/converter';
import { calculateGpsBounds, calculateFitMapSettings } from '../utils/presenter.utils';
import { NodePlan } from '../../../planBT/types/node.types';
import { LinkPlan } from '../../../planBT/types/link.types';
import { StreetLight } from '../../../planBT/types/streetLight.types';

interface DataManagementProps {
  getAllNodesQuery: any;
  allLinkQuery: any;
  getAllStreetLightsQuery: any;
  setElements: React.Dispatch<React.SetStateAction<GridElement[]>>;
  setConnections: React.Dispatch<React.SetStateAction<Connection[]>>;
  setGlobalGpsBounds: React.Dispatch<React.SetStateAction<GpsBounds>>;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  setPan: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  setMapCenter: React.Dispatch<React.SetStateAction<{ lat: number; lng: number }>>;
}

export const useDataManagement = ({
  getAllNodesQuery,
  allLinkQuery,
  getAllStreetLightsQuery,
  setElements,
  setConnections,
  setGlobalGpsBounds,
  setZoom,
  setPan,
  setMapCenter
}: DataManagementProps) => {

  /**
   * Effect to load and convert node data from the API
   * Converts database node format to grid editor element format
   * Integrates street lights with POLE nodes based on str_light_pole and node_hosted_id relationship
   */
  useEffect(() => {
    // Process node data when available
    if (getAllNodesQuery.data?.data) {
      const allNodes = getAllNodesQuery.data.data;
      const streetLights = getAllStreetLightsQuery.data?.data || [];
      
      // Convert each database node to a grid element
      const convertedElements = allNodes.map((node: NodePlan) => {
        const converted = convertDBDataNodeToGridElement(node, allNodes);
        
        // If this is a POLE node, find associated street lights
        if (node.node_type === 'POLE') {
          console.log('🔍 Processing POLE node:', node.node_host_name, 'hosted_id:', node.node_hosted_id);
          const associatedStreetLights = streetLights.filter((streetLight: StreetLight) => 
            streetLight.str_light_pole === node.node_hosted_id
          );
          
          console.log('💡 Found', associatedStreetLights.length, 'street lights for pole', node.node_host_name);
          if (associatedStreetLights.length > 0) {
            console.log('💡 Street lights details:', associatedStreetLights);
          }
          
          // Convert street lights to Light format for the pole
          if (associatedStreetLights.length > 0) {
            const lightsArray = associatedStreetLights.map((streetLight: StreetLight) => ({
              id: streetLight.str_light_id,
              type: 'street-light' as const,
              status: 'on' as const,
              power: 50,
              name: streetLight.str_light_name,
              number: streetLight.str_light_number,
              zhagaDevui: streetLight.str_light_zhaga_devui,
              poleId: node.node_hosted_id // Assigner le hostId du nœud comme poleId
            }));
            
            // Explicitly assign the lights array to the converted element
            converted.lights = lightsArray;
            console.log('💡 Converted lights for pole', node.node_host_name, ':', converted.lights);
            console.log('💡 Element after lights assignment:', converted);
          } else {
            // Ensure lights is an empty array for poles without street lights
            converted.lights = [];
            console.log('💡 No street lights found for pole', node.node_host_name, '- setting empty array');
          }
        }
        
        // Mark elements loaded from API as UN_CHANGED initially
        converted.action = ElementAction.UN_CHANGED;
        return converted;
      });
      
      setElements(convertedElements);
      
      // Calculate and store global GPS bounds for coordinate conversion
      const bounds = calculateGpsBounds(convertedElements);
      console.log('🌍 Setting global GPS bounds:', bounds);
      setGlobalGpsBounds(bounds);
    } else if (getAllNodesQuery.data && getAllNodesQuery.data.data && getAllNodesQuery.data.data.length === 0) {
      // Handle empty plan case - calculateGpsBounds will return default bounds
      console.log('🌍 No elements found, setting default GPS bounds for Paris area');
      const bounds = calculateGpsBounds([]);
      setGlobalGpsBounds(bounds);
      setElements([]);
    }
  }, [getAllNodesQuery.data, getAllStreetLightsQuery.data, setElements, setGlobalGpsBounds]);

  /**
   * Effect to load and convert link/connection data from the API
   * Converts database link format to grid editor connection format
   */
  useEffect(() => {
    // Process link data when available
    if (allLinkQuery.data?.data) {
      // Convert each database link to a grid connection
      const convertedConnections = allLinkQuery.data.data.map((link: LinkPlan) => {
        const converted = convertDBDataLinkToConnection(link);
        // Mark connections loaded from API as UN_CHANGED initially
        converted.action = ElementAction.UN_CHANGED;
        return converted;
      });
      
      setConnections(convertedConnections);
    }
  }, [allLinkQuery.data, setConnections]);

  /**
   * Effect to auto-center the map when elements are first loaded
   * Centers the map on the area with the most points and sets optimal zoom
   */
  useEffect(() => {
    if (getAllNodesQuery.data?.data) {
      const allNodes = getAllNodesQuery.data.data;
      
      // Convert nodes to grid elements for centering calculation
      const convertedElements = allNodes.map((node: NodePlan) => {
        const converted = convertDBDataNodeToGridElement(node, allNodes);
        converted.action = ElementAction.UN_CHANGED;
        return converted;
      });
      
      // Calculate GPS bounds from the elements
      const bounds = calculateGpsBounds(convertedElements);
      
      if (bounds) {
        // Calculate optimal map settings (center + zoom) based on element distribution
        const mapSettings = calculateFitMapSettings(bounds, convertedElements);
        
        // Apply the calculated settings with a delay to ensure Leaflet is ready
        setTimeout(() => {
          setMapCenter(mapSettings.mapCenter);
          setZoom(mapSettings.zoom);
          setPan(mapSettings.pan);
        }, 300);
      } else {
        // Fallback: reset to default view with maximum zoom for street level
        setTimeout(() => {
          setZoom(3.0);
          setPan({ x: 0, y: 0 });
        }, 300);
      }
    }
  }, [getAllNodesQuery.data, setMapCenter, setZoom, setPan]);

  return {
    isLoadingNodes: getAllNodesQuery.isLoading,
    isLoadingLinks: allLinkQuery.isLoading,
    isLoadingStreetLights: getAllStreetLightsQuery.isLoading
  };
};
